import React, { useEffect, useRef, useState } from 'react';

// Declare global L (Leaflet) to avoid TypeScript errors
declare global {
  interface Window {
    L: any;
  }
}

interface LocationPickerProps {
  initialLatitude?: number;
  initialLongitude?: number;
  onLocationChange: (latitude: number, longitude: number) => void;
  address: string;
  className?: string;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  initialLatitude = 51.505,
  initialLongitude = -0.09,
  onLocationChange,
  address,
  className = ''
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [currentLat, setCurrentLat] = useState(initialLatitude);
  const [currentLng, setCurrentLng] = useState(initialLongitude);

  // Load Leaflet CSS and JS
  useEffect(() => {
    const loadLeaflet = async () => {
      // Check if Leaflet is already loaded
      if (window.L) {
        setIsMapLoaded(true);
        return;
      }

      // Load Leaflet CSS
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      cssLink.crossOrigin = '';
      document.head.appendChild(cssLink);

      // Load Leaflet JS
      return new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = () => {
          setIsMapLoaded(true);
          resolve();
        };
        document.body.appendChild(script);
      });
    };

    loadLeaflet();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || mapInstanceRef.current) return;

    const map = window.L.map(mapRef.current).setView([initialLatitude, initialLongitude], 15);

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Create draggable marker
    const marker = window.L.marker([initialLatitude, initialLongitude], {
      draggable: true
    }).addTo(map);

    // Add popup with address
    marker.bindPopup(`<strong>Store Location</strong><br/>${address}`).openPopup();

    // Handle marker drag
    marker.on('dragend', function(e: any) {
      const position = e.target.getLatLng();
      setCurrentLat(position.lat);
      setCurrentLng(position.lng);
      onLocationChange(position.lat, position.lng);
    });

    // Handle map click to move marker
    map.on('click', function(e: any) {
      const { lat, lng } = e.latlng;
      marker.setLatLng([lat, lng]);
      setCurrentLat(lat);
      setCurrentLng(lng);
      onLocationChange(lat, lng);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isMapLoaded, initialLatitude, initialLongitude, address, onLocationChange]);

  // Update marker position when initial coordinates change
  useEffect(() => {
    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setLatLng([initialLatitude, initialLongitude]);
      mapInstanceRef.current.setView([initialLatitude, initialLongitude], 15);
      setCurrentLat(initialLatitude);
      setCurrentLng(initialLongitude);
    }
  }, [initialLatitude, initialLongitude]);

  if (!isMapLoaded) {
    return (
      <div className={`location-picker-loading ${className}`}>
        <div className="loading-spinner"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  return (
    <div className={`location-picker-container ${className}`}>
      <div className="location-info">
        <p><strong>Address:</strong> {address}</p>
        <p><strong>Coordinates:</strong> {currentLat.toFixed(6)}, {currentLng.toFixed(6)}</p>
        <p className="instruction">💡 Drag the marker or click on the map to adjust the exact location</p>
      </div>
      <div 
        ref={mapRef} 
        className="location-picker-map"
        style={{ height: '400px', width: '100%', borderRadius: '12px' }}
      />
    </div>
  );
};

export default LocationPicker;